<template>
  <!-- manual ticket create, shared by the Coaching Board and Coaching
       Tickets: the same create_coaching_ticket call the Flagged /
       Recognition pages use, minus the scorecard snapshot — source is
       'manual' so the dashboard's source mix stays honest. parents open it
       with $refs.<ref>.open() and reload on @created. -->
  <b-modal id="cp-add-ticket-modal" hide-footer title="Add a coaching ticket">
    <div class="cat-label">Type</div>
    <b-form-radio-group v-model="form.kind" class="mb-3">
      <b-form-radio value="coaching">Coaching</b-form-radio>
      <b-form-radio value="recognition">Recognition</b-form-radio>
    </b-form-radio-group>

    <div class="cat-label">Driver</div>
    <!-- datalist, not a select: suggestions come from drivers already in
         the queue, but a manual ticket is often FOR a driver no feed has
         seen yet, so free text must stay possible (od-assigned precedent) -->
    <b-form-input v-model="form.driverName" size="sm" class="mb-3"
                  placeholder="Driver name" list="cat-driver-list" />
    <datalist id="cat-driver-list">
      <option v-for="d in knownDrivers" :key="'kd-' + d.name" :value="d.name"></option>
    </datalist>

    <div class="cat-label">Topic</div>
    <b-form-input v-model="form.topic" size="sm" class="mb-3"
                  placeholder="e.g. Harsh braking" list="cat-topic-list" />
    <datalist id="cat-topic-list">
      <option v-for="topic in knownTopics" :key="'kt-' + topic" :value="topic"></option>
    </datalist>

    <div class="cat-label">{{ form.kind === 'recognition' ? 'Reason' : 'Why' }} <span class="cat-hint">optional — it goes on the record</span></div>
    <b-form-textarea v-model="form.why" rows="2" class="mb-3"
                     :placeholder="form.kind === 'recognition'
                       ? 'What they did well'
                       : 'What happened, and what the conversation should cover'" />

    <!-- recognition follows the Recognize flow: no assignee or due date —
         it queues for delivery and the backend applies its defaults -->
    <template v-if="form.kind !== 'recognition'">
      <div class="cat-label">Assign to</div>
      <b-form-select v-model="form.assignTo" :options="contactOptions" size="sm" class="mb-3" />

      <div class="cat-label">Due date</div>
      <b-form-input v-model="form.dueDate" type="date" size="sm" class="mb-3 cat-date" />
    </template>

    <div class="d-flex justify-content-end">
      <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('cp-add-ticket-modal')">Cancel</b-button>
      <b-button variant="primary" :disabled="saving || !canSave" @click="save">
        {{ saving ? 'Saving…' : 'Create ticket' }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { create_coaching_ticket, get_contact_users } from '../networking/apis'

export default {
  name: 'coaching_add_ticket_modal',
  props: {
    // the acting tenant, resolved by the parent page (resolveActingTenant) —
    // parents MUST resetTenantScope() on tenant change before reopening
    tenant: { type: [String, Number], required: true },
    // the loaded queue, for the driver/topic datalist suggestions
    tickets: { type: Array, default: () => [] },
    // tenant SLA settings riding back on the list response (may be null
    // before first load) — feeds the due-date prefill
    settings: { type: Object, default: null },
  },
  data() {
    return {
      form: { kind: 'coaching', driverName: '', topic: '', why: '', assignTo: null, dueDate: '' },
      saving: false,
      contacts: [],
      contactsLoaded: false,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    // datalist suggestions from drivers already in the queue; also the
    // driver_id lookup so a picked name keeps its id
    knownDrivers() {
      const by = {}
      this.tickets.forEach(t => {
        if (!t.driver_name) return
        by[t.driver_name] = by[t.driver_name] || { name: t.driver_name, driver_id: t.driver_id || null }
      })
      return Object.values(by).sort((a, b) => a.name.localeCompare(b.name))
    },
    knownTopics() {
      const seen = new Set()
      this.tickets
        .filter(t => (t.kind || 'coaching') === this.form.kind)
        .forEach(t => { if (t.topic) seen.add(t.topic) })
      return [...seen].sort((a, b) => a.localeCompare(b))
    },
    // the assignee picker, mirroring the scope mixin's Assign modal: the
    // contacts feed (it already excludes drivers) with the acting user first
    contactOptions() {
      const me = this.user.username
      const options = this.contacts
        .filter(c => c.username !== me)
        .map(c => ({
          value: c.username,
          text: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.username,
        }))
      return [
        { value: me, text: `${this.user.full_name || me} (me)` },
        ...options,
      ]
    },
    canSave() {
      return !!(this.form.driverName.trim() && this.form.topic.trim())
    },
  },
  methods: {
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },
    open() {
      // same due-date prefill as the Flagged page's Assign modal: the
      // tenant's own SLA setting, riding back on the list response
      const dueDays = (this.settings && this.settings.due_days_default) || 5
      this.form = {
        kind: 'coaching',
        driverName: '',
        topic: '',
        why: '',
        assignTo: this.user.username,
        dueDate: moment().add(dueDays, 'days').format('YYYY-MM-DD'),
      }
      this.loadContacts()
      this.$bvModal.show('cp-add-ticket-modal')
    },
    // tenant changed under us: close, and drop the old tenant's contacts
    resetTenantScope() {
      this.$bvModal.hide('cp-add-ticket-modal')
      this.contacts = []
      this.contactsLoaded = false
    },
    async loadContacts() {
      if (this.contactsLoaded) return
      this.contactsLoaded = true
      try {
        const resp = await get_contact_users(this.tenant)
        this.contacts = ((resp.value && resp.value.data) || {}).results || []
      } catch (e) {
        // the picker degrades to "me"; retry on the next open
        this.contactsLoaded = false
      }
    },
    async save() {
      if (!this.canSave) return
      const form = this.form
      const driverName = form.driverName.trim()
      const isRecognition = form.kind === 'recognition'
      // a picked suggestion keeps its driver_id so the backend's dual-keyed
      // duplicate check sees this ticket from both partitions
      const known = this.knownDrivers.find(d => d.name === driverName)
      const assignee = isRecognition
        ? null : this.contactOptions.find(o => o.value === form.assignTo)
      this.saving = true
      try {
        await create_coaching_ticket(this.tenant, {
          driver_id: (known && known.driver_id) || null,
          driver_name: driverName,
          kind: form.kind,
          topic: form.topic.trim(),
          why: form.why.trim() || undefined,
          source: 'manual',
          // omitted when unset so the backend keeps defaulting to the actor
          assigned_to: assignee ? assignee.value : undefined,
          assigned_to_name: assignee ? assignee.text.replace(/ \(me\)$/, '') : undefined,
          due_date: (!isRecognition && form.dueDate) || undefined,
        })
        this.$bvModal.hide('cp-add-ticket-modal')
        this.$bvToast.toast(
          isRecognition
            ? `${driverName} added to the recognition lane — sign it off once it's been delivered.`
            : `Coaching raised for ${driverName} — it is now in the queue as Open.`,
          { title: 'Coaching Portal', variant: 'success', autoHideDelay: 4000 },
        )
        this.$emit('created')
      } catch (e) {
        // the duplicate-open-ticket rejection surfaces here with the
        // existing ticket's number in the server message
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not create the ticket.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.cat-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cat-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cat-date { max-width: 180px; }
</style>
